import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSuperAdmin } from "@/contexts/SuperAdminContext";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import {
  Building2, LayoutDashboard, Users, CreditCard, Settings, LogOut, Menu, X,
  ChevronRight, FileText, FileStack, UserPlus, Clock, ToggleRight, MessageSquare,
  Receipt
} from "lucide-react";

const navSections = [
  { heading: "Overview", ar: "نظرة عامة", items: [
    { path: "/platform", icon: LayoutDashboard, label: "Dashboard", ar: "لوحة التحكم" },
  ]},
  { heading: "Tenants", ar: "المستأجرون", items: [
    { path: "/platform/tenants", icon: Building2, label: "Tenants", ar: "المستأجرون" },
    { path: "/platform/signup-requests", icon: UserPlus, label: "Signup Requests", ar: "طلبات التسجيل" },
    { path: "/platform/tenant-users", icon: Users, label: "Tenant Users", ar: "مستخدمو المستأجرين" },
  ]},
  { heading: "Billing", ar: "الفوترة", items: [
    { path: "/platform/plans", icon: CreditCard, label: "Plans", ar: "الخطط" },
    { path: "/platform/subscriptions", icon: Receipt, label: "Subscriptions", ar: "الاشتراكات" },
    { path: "/platform/trials", icon: Clock, label: "Trials", ar: "التجارب" },
  ]},
  { heading: "Configuration", ar: "الإعدادات", items: [
    { path: "/platform/templates", icon: FileStack, label: "Templates", ar: "القوالب" },
    { path: "/platform/modules", icon: ToggleRight, label: "Module Access", ar: "صلاحيات الوحدات" },
  ]},
  { heading: "System", ar: "النظام", items: [
    { path: "/platform/activity-logs", icon: FileText, label: "Activity Logs", ar: "سجلات النشاط" },
    { path: "/platform/support-notes", icon: MessageSquare, label: "Support Notes", ar: "ملاحظات الدعم" },
    { path: "/platform/settings", icon: Settings, label: "Settings", ar: "الإعدادات" },
  ]},
];

export function SuperAdminLayout({ children }) {
  const { admin, logout } = useSuperAdmin();
  const { lang, toggleLang } = useLang();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => {
    if (path === "/platform") return location.pathname === "/platform";
    return location.pathname.startsWith(path);
  };

  const NavContent = ({ onItemClick }) => (
    <>
      {navSections.map((section) => (
        <div key={section.heading} className="mb-1">
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            {lang === "ar" ? section.ar : section.heading}
          </p>
          {section.items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onItemClick}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                isActive(item.path)
                  ? "bg-th-surface text-cream-900 font-semibold"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive(item.path) ? "text-cream-900" : "text-slate-400"}`} />
              {lang === "ar" ? item.ar : item.label}
              {isActive(item.path) && <ChevronRight className="w-3.5 h-3.5 ml-auto text-th-dim" />}
            </Link>
          ))}
        </div>
      ))}
      <div className="px-3 pt-2">
        <button onClick={toggleLang} data-testid="platform-lang-toggle"
          className="w-full px-3 py-2 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 transition text-left">
          {lang === "en" ? "عربي" : "English"}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-60 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-slate-200 pt-4 pb-3 overflow-y-auto">
          <div className="flex items-center gap-2.5 px-4 mb-5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center shadow-sm">
              <Building2 className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900 text-sm">Platform Admin</h1>
              <p className="text-[10px] text-slate-400">SaaS Control Panel</p>
            </div>
          </div>
          <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
            <NavContent />
          </nav>
          <div className="px-3 mt-2 border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                {admin?.name?.charAt(0)?.toUpperCase() || "A"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-800 truncate">{admin?.name}</p>
                <p className="text-[10px] text-slate-400 truncate">{admin?.email}</p>
              </div>
            </div>
            <Button variant="ghost" onClick={logout} data-testid="admin-logout-btn"
              className="w-full mt-2 h-8 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 justify-start gap-2">
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center gap-3 bg-white border-b border-slate-200 px-4 py-3 shadow-sm">
        <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-slate-700 hover:bg-slate-100 p-1">
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
            <Building2 className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-semibold text-slate-900 text-sm">Platform Admin</span>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-white pt-14 overflow-y-auto">
          <nav className="px-3 py-4">
            <NavContent onItemClick={() => setMobileMenuOpen(false)} />
            <Button variant="ghost" onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="w-full mt-4 text-slate-500 hover:text-red-600 hover:bg-red-50 justify-start gap-2">
              <LogOut className="w-4 h-4" /> Sign Out
            </Button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:pl-60">
        <div className="px-4 py-5 sm:px-6 lg:px-8">{children}</div>
      </main>
    </div>
  );
}
